import {
  PageContainer,
  PageHeader,
  PageHeaderLeading,
  DAILY_INTERVIEW_ENABLED,
  useAgentListening,
  useCreateDiscourseSession,
  useDiscourseSession,
  useDiscourseSupportThread,
  setDiscourseListeningAgentContext,
} from '@yeshuman/sdk'
import { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { InterviewKitPanel } from '../interview/interview-kit-panel'
import { InterviewLivePanel } from '../interview/interview-live-panel'

/**
 * Interviewer live video session — Daily room; the agent coaches in the bound thread.
 */
export function InterviewerSessionPage() {
  const { sessionId } = useParams<{ sessionId?: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const createSession = useCreateDiscourseSession()
  const [bootError, setBootError] = useState<string | null>(null)
  const [joinToken, setJoinToken] = useState<string | null>(null)
  const { setListening, clearListening } = useAgentListening()
  const supportThreadId = useDiscourseSupportThread(sessionId)
  const sessionQuery = useDiscourseSession(sessionId)
  const sessionEnded = (sessionQuery.data?.status || '').toLowerCase() === 'completed'

  const profileId = searchParams.get('profileId')
  const opportunityId = searchParams.get('opportunityId')
  const briefId = searchParams.get('briefId')
  const candidateLabel = searchParams.get('label')
  const interviewId = searchParams.get('interviewId')
  const applicationId = searchParams.get('applicationId')

  useEffect(() => {
    if (sessionId || createSession.isPending || createSession.isSuccess) return
    if (!DAILY_INTERVIEW_ENABLED) {
      setBootError('Live video interviews are not enabled for this tenant.')
      return
    }
    const title = candidateLabel
      ? `Interview · ${candidateLabel}`
      : 'Interview'
    createSession.mutate(
      {
        kind: 'interview',
        modality: 'video',
        title,
        profile_id: profileId,
        subject: opportunityId ? { type: 'opportunities.opportunity', id: opportunityId } : null,
        related: briefId ? [{ type: 'briefs.brief', id: briefId }] : [],
        classifier_profile_id: 'interview_coverage',
        demo_mode: false,
        metadata: interviewId ? { interview_id: interviewId } : undefined,
      },
      {
        onSuccess: (session) => {
          if (session.join_token) {
            setJoinToken(session.join_token)
            try {
              sessionStorage.setItem(
                `yh.interview.joinToken.${session.id}`,
                session.join_token,
              )
            } catch {
              /* ignore */
            }
          }
          const qs = window.location.search
          navigate(`/interviewer/session/${session.id}${qs}`, { replace: true })
        },
        onError: () =>
          setBootError('Could not create the interview room. Check Daily configuration.'),
      },
    )
  }, [
    sessionId,
    createSession,
    navigate,
    profileId,
    opportunityId,
    briefId,
    candidateLabel,
    interviewId,
  ])

  useEffect(() => {
    if (!sessionId) {
      clearListening()
      setDiscourseListeningAgentContext(null, null)
      return
    }
    setListening({
      sessionId,
      profileId: 'interview_coverage',
      capability: 'coach_hints',
      modeLabel: 'Interview support',
      label: candidateLabel || undefined,
      supportThreadId,
      ended: sessionEnded,
    })
    setDiscourseListeningAgentContext(sessionId, 'interview_coverage')
    return () => {
      clearListening()
      setDiscourseListeningAgentContext(null, null)
    }
  }, [sessionId, candidateLabel, supportThreadId, sessionEnded, setListening, clearListening])

  const contextLabel = [
    candidateLabel ? `Candidate: ${candidateLabel}` : null,
    opportunityId ? `Opportunity: ${opportunityId.slice(0, 8)}…` : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <PageContainer maxWidth="full">
      <PageHeader
        leading={<PageHeaderLeading />}
        title="Interview"
        description={
          contextLabel ||
          'Live video with the candidate. Copy the guest link; coaching arrives in the agent thread.'
        }
      />
      {bootError ? (
        <p className="text-sm text-destructive" role="alert">
          {bootError}
        </p>
      ) : !sessionId ? (
        <p className="text-sm text-muted-foreground">Starting interview room…</p>
      ) : briefId ? (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(18rem,24rem)]">
          <InterviewLivePanel sessionId={sessionId} initialJoinToken={joinToken} />
          <InterviewKitPanel
            briefId={briefId}
            applicationId={applicationId}
            candidateName={candidateLabel || undefined}
            interviewId={interviewId}
            className="lg:max-h-[calc(100dvh-10rem)] lg:overflow-y-auto"
          />
        </div>
      ) : (
        <InterviewLivePanel sessionId={sessionId} initialJoinToken={joinToken} />
      )}
    </PageContainer>
  )
}
